import { noop } from 'utils/fp';
import { PopoverStateReturn } from 'components/Popover';
import { accountMenuItem, accountPopoverLayout } from 'components/Navbar/Navbar.styles';
import { AccountSelector, PopoverButton, PopoverLink, PopoverSection } from 'components/Navbar';
import { usePathWithoutQueryParams } from 'components/Navbar/Navbar.hooks';
import { IconBug, IconLogout, IconPlus } from 'components/Icons';
import { JiraButton, JiraLinks } from 'components/Jira';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { useAccountPopoverLinks, useSignOutWithAnalytics } from './hooks';

export interface DesktopMenuItemsProps {
  onLogout: React.MouseEventHandler<HTMLButtonElement>;
  closePopup?: () => void;
}

export interface CommonMenuItemsProps extends Pick<DesktopMenuItemsProps, 'closePopup'> {
  itemClassName?: string;
}

export interface MobileMenuItemsProps {
  onLogout: () => void;
  canCreateApp: boolean;
  handleCreateApp: () => void;
  closePopup?: () => void;
}

export interface AccountPopoverProps extends Omit<DesktopMenuItemsProps, 'closePopup'> {
  popover: PopoverStateReturn;
}

export interface PopoverLinkProps {
  href: string;
  text: string;
  className?: string;
  badge?: string | undefined;
  externalLink?: boolean;
}

function LogoutSection(props: Pick<DesktopMenuItemsProps, 'onLogout'>) {
  const { trackSignOut } = useSignOutWithAnalytics();

  const handleSignOut = (event: React.MouseEvent<HTMLButtonElement>) => {
    trackSignOut();
    props.onLogout?.(event);
  };

  return (
    <PopoverSection>
      <PopoverButton className={accountMenuItem} text="Sign out" onClick={handleSignOut} icon={IconLogout} />
    </PopoverSection>
  );
}

function JiraSection() {
  const { track } = useDataCollection();

  const onHelpMenuClick = (btnText: string): void => {
    track('Help Menu Clicked', { param1: 'main_header', param2: btnText });
  };

  return (
    <PopoverSection>
      {/* <JiraButton collectorId={JiraLinks.sendFeedback.collectorId} clickTargetSelector={JiraLinks.sendFeedback.clickTargetSelector}>
        <PopoverButton text="Send feedback" icon={IconMessageSquareV2} onClick={() => onHelpMenuClick('Send feedback')} />
      </JiraButton> */}
      <JiraButton collectorId={JiraLinks.bugReport.collectorId} clickTargetSelector={JiraLinks.bugReport.clickTargetSelector}>
        <PopoverButton text="Report a Bug" icon={IconBug} onClick={() => onHelpMenuClick('Report a Bug')} />
      </JiraButton>
    </PopoverSection>
  );
}

function CommonMenuItems(props: CommonMenuItemsProps) {
  const path = usePathWithoutQueryParams();
  const sections = useAccountPopoverLinks();

  return (
    <>
      {sections.map(({ id, links, title }) => {
        return (
          <PopoverSection key={id} title={title}>
            {links.map(({ text, handleClick, url, badge, externalLink, icon }) => {
              return url && url !== path ? (
                <PopoverLink
                  className={props.itemClassName}
                  href={url}
                  text={text}
                  icon={icon}
                  badge={badge}
                  key={`${id}_${url}`}
                  externalLink={externalLink}
                  onClick={handleClick || props.closePopup}
                />
              ) : (
                <PopoverButton
                  className={props.itemClassName}
                  text={text}
                  icon={icon}
                  badge={badge}
                  onClick={handleClick || props.closePopup}
                  key={`${id}_${text}`}
                />
              );
            })}
          </PopoverSection>
        );
      })}
      <JiraSection />
    </>
  );
}

export const MobileMenuItems: React.FC<MobileMenuItemsProps> = ({ onLogout, closePopup = noop, canCreateApp, handleCreateApp }) => {
  return (
    <section className={accountPopoverLayout}>
      <AccountSelector onClose={closePopup} />
      {canCreateApp && (
        <PopoverSection>
          <PopoverButton className={accountMenuItem} text="Create Application" onClick={handleCreateApp} icon={IconPlus} />
        </PopoverSection>
      )}
      <CommonMenuItems closePopup={closePopup} itemClassName={accountMenuItem} />
      <LogoutSection onLogout={onLogout} />
    </section>
  );
};

export const DesktopMenuItems: React.FC<DesktopMenuItemsProps> = ({ onLogout, closePopup = noop }) => {
  return (
    <>
      <AccountSelector onClose={closePopup} />
      <CommonMenuItems closePopup={closePopup} />
      <LogoutSection onLogout={onLogout} />
    </>
  );
};
